import React from "react";
import "./userForm.scss";
import i18n from "../../../i18n/i18n";
import TextInput from "../../../common/components/shared/textInput/textInput";
import {ValidatedValue} from "../../../helpers/validatedValue";
import Validator from "../../../helpers/validator";
import {errorMessages} from "../../../helpers/errorMessage";
import DropDown from "../../../common/components/shared/dropDown/dropDown";
import RadioGroup from "../../../common/components/shared/radioGroup/radioGroup";
import {DropDownModel} from "../../../common/models/dropDown";
import {RadioModel} from "../../../common/models/radio";
import Checkbox from "../../../common/components/shared/checkbox/checkbox";
import {UserInManagementRoles} from "../../../helpers/userRoles";
import {HttpError} from "../../../common/components/shared/httpError/httpError";
import {routingAddresses} from "../../../helpers/routing";
import Button from "../../../common/components/shared/button/button";
import {Api} from "../../../helpers/api";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../common/models/httpError";
import {RouteComponentProps, withRouter} from "react-router";
import {AdministrationService} from "../../services/administrationService";
import {UserInManagementModel} from "../../models/user";
import ResetPasswordPopup from "../resetPasswordPopup/resetPasswordPopup";
import Password from "../../../common/components/shared/password/password";
import {IUserStore} from "../../stores/userStore";
import {storesContext} from "../../../common/stores/stores";
import Form from "../../../common/components/shared/form/form";
import {Utils} from "../../../helpers/utils";
import ChangeUserPasswordPopup from "../changeUserPasswordPopup/changeUserPasswordPopup";
import DisablePopup from "../usersList/disablePopup/disablePopup";
import EnablePopup from "../usersList/enablePopup/enablePopup";

interface UserFormProps extends RouteComponentProps {
    isUpdate: boolean;
    userData?: UserInManagementModel | null;
}

interface UserFormState {
    username: ValidatedValue<string>;
    language: ValidatedValue<string>;
    password: ValidatedValue<string>;
    confirmPassword: ValidatedValue<string>;
    role: ValidatedValue<UserInManagementRoles | null | string>;
    isHelpdesk: ValidatedValue<boolean>;
    isSubmitButtonBlocked: boolean;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    displayPopup: "resetPasswordPopup" | "changePasswordPopup" | "enablePopup" | "disablePopup" | null;
    resetPasswordResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    isPopupActionWaiting: boolean;
    disableUserResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    enableUserResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
}

class UserForm extends React.Component <UserFormProps, UserFormState> {
    static contextType = storesContext;

    constructor(props: UserFormProps) {
        super(props);

        const notEmptyValidator = {
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        };

        const passwordValidator = {
            name: "passwordIsCorrect",
            fn: Validator.isCorrectPassword,
            errorMessage: () => `${i18n.t("administration.addUser.errors.incorrectPassword")}`
        };

        this.state = {
            username: new ValidatedValue("", [notEmptyValidator]),
            language: new ValidatedValue("", [notEmptyValidator]),
            password: new ValidatedValue("", [notEmptyValidator, passwordValidator]),
            confirmPassword: new ValidatedValue("", [notEmptyValidator]),
            role: new ValidatedValue(null, [notEmptyValidator]),
            isHelpdesk: new ValidatedValue(false),
            isSubmitButtonBlocked: false,
            response: undefined,
            displayPopup: null,
            resetPasswordResponse: undefined,
            isPopupActionWaiting: false,
            enableUserResponse: undefined,
            disableUserResponse: undefined
        };

        const confirmPasswordValidator = {
            name: "confirmPasswordIsCorrect",
            fn: (value: string) => {
                return (value === this.state.password.value);
            },
            errorMessage: () => `${i18n.t("administration.addUser.errors.incorrectConfirmPassword")}`
        };
        this.state.confirmPassword.addValidators(confirmPasswordValidator);
    }

    async componentDidMount() {
        if (this.props.isUpdate) {
            this.setUserData();
        }
    }

    render() {
        return (
            <Form
                class="user-form"
                renderError={this.renderErrors()}
                renderContent={this.renderContent()}
                isSubmitButtonBlocked={this.state.isSubmitButtonBlocked}
                backAddress={routingAddresses.administration.list}
                submit={this.submit.bind(this)}
                saveButtonText={this.props.isUpdate ? i18n.t("save") : i18n.t("administration.addUser.add")}
            />
        );
    }

    protected renderContent() {
        return (
            <div className="form-sections">
                {this.renderUsernameAndPasswordSection()}
                {this.renderPermissionsSectionSection()}
            </div>
        );
    }

    protected renderErrors() {
        if(!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={this.props.isUpdate ? i18n.t("administration.modifyUser.errors.modifyUserErrorSuffix") :
                        i18n.t("administration.addUser.errors.addUserErrorSuffix")}
                    translatedFields={{
                        username: i18n.t("administration.addUser.usernameAndPasswordSection.username"),
                        language: i18n.t("administration.addUser.usernameAndPasswordSection.language"),
                        password: i18n.t("administration.addUser.usernameAndPasswordSection.password"),
                        role: i18n.t("administration.addUser.permissionsSection.role")
                    }}
                />
            );
        }
    }

    private renderUsernameAndPasswordSection() {
        return (
            <div className={"username-and-password-section"}>
                <div className="username-and-password-header">
                    {i18n.t("administration.addUser.usernameAndPasswordSection.header")}
                </div>
                <TextInput
                    label={`${i18n.t("administration.addUser.usernameAndPasswordSection.username")}*`}
                    validatedValue={this.state.username}
                    testId="username"
                    disabled={this.props.isUpdate}
                />

                <DropDown
                    label={`${i18n.t("administration.addUser.usernameAndPasswordSection.language")}*`}
                    validatedValue={this.state.language}
                    items={this.languages}
                    testId="language"
                />

                {this.renderActions()}
            </div>
        );
    }

    private renderActions() {
        const userStore = this.context.userStore as IUserStore;

        if (this.props.isUpdate) {
            return (
                <div className="actions">
                    {userStore.canChangeOrResetPassword && (
                        <>
                            {this.renderResetPasswords()}
                            {this.renderChangeUserPassword()}
                        </>
                    )}
                    {this.props.userData && (
                        this.renderEnableOrDisableActions(this.props.userData)
                    )}
                </div>
            );
        } else {
            return this.renderPasswords();
        }
    }

    private renderResetPasswords() {
        return(
            <>
                <Button
                    className="reset-password-btn"
                    text={`${i18n.t("administration.modifyUser.resetPassword")}`}
                    type="secondary"
                    variant="text"
                    onClick={() => {
                        this.onResetOrChangePasswordAction("resetPasswordPopup");
                    }}
                    testId="reset-password-button"/>
                <ResetPasswordPopup
                    displayPopup={this.state.displayPopup === "resetPasswordPopup"}
                    closePopup={this.closePopup.bind(this)}
                    confirm={this.confirmResetPassword.bind(this)}
                    username={this.props.userData?.username || ""}
                    response={this.state.resetPasswordResponse}
                    isWaiting={this.state.isPopupActionWaiting}
                />
            </>
        );
    }

    private renderEnableOrDisableActions(user: UserInManagementModel) {
        return (
            <>
                {user.enabled ? (
                    <Button
                        className="disable-button"
                        text={i18n.t("administration.actions.disable")}
                        testId="disable-button"
                        onClick={() => {
                            this.setState({displayPopup: "disablePopup"});
                        }}
                        type="secondary"
                        variant="text"
                    />
                ) : (
                    <Button
                        className="enable-button"
                        text={i18n.t("administration.actions.enable")}
                        testId="enable-button"
                        onClick={() => {
                            this.setState({displayPopup: "enablePopup"});
                        }}
                        type="secondary"
                        variant="text"
                    />
                )}
                <DisablePopup
                    displayPopup={this.state.displayPopup === "disablePopup"}
                    closePopup={this.closePopup.bind(this)}
                    confirm={this.confirmEnableOrDisable.bind(this, "disablePopup", false)}
                    username={user.username || ""}
                    response={this.state.disableUserResponse}
                    isWaiting={this.state.isPopupActionWaiting}
                />
                <EnablePopup
                    displayPopup={this.state.displayPopup === "enablePopup"}
                    closePopup={this.closePopup.bind(this)}
                    confirm={this.confirmEnableOrDisable.bind(this, "enablePopup", true)}
                    username={user.username || ""}
                    response={this.state.enableUserResponse}
                    isWaiting={this.state.isPopupActionWaiting}
                />
            </>
        );
    }

    private renderChangeUserPassword() {
        return(
            <>
                <Button
                    className="change-password-btn"
                    text={`${i18n.t("administration.modifyUser.changePassword")}`}
                    type="secondary"
                    variant="text"
                    onClick={() => {
                        this.onResetOrChangePasswordAction("changePasswordPopup");
                    }}
                    testId="change-password-button"/>
                <ChangeUserPasswordPopup
                    displayPopup={this.state.displayPopup === "changePasswordPopup"}
                    closePopup={this.closePopup.bind(this)}
                    username={this.props.userData?.username || ""}
                    userId={this.props.userData!.id}
                    confirmSuccessAction={() => {
                        this.setState({
                            displayPopup: null
                        });
                    }}
                />
            </>
        );
    }

    private renderPasswords() {
        return (
            <>
                <Password
                    password={this.state.password}
                    label={`${i18n.t("administration.addUser.usernameAndPasswordSection.password")}*`}
                    testId="password"
                />
                <Password
                    password={this.state.confirmPassword}
                    label={`${i18n.t("administration.addUser.usernameAndPasswordSection.confirmPassword")}*`}
                    testId="confirm-password"
                />
            </>
        );
    }

    private renderPermissionsSectionSection() {
        return (
            <div className="permissions-section">
                <div className="permissions-header">
                    {i18n.t("administration.addUser.permissionsSection.header")}
                </div>
                <RadioGroup
                    testId="roles"
                    label={`${i18n.t("administration.addUser.permissionsSection.role")}*`}
                    items={this.roles}
                    validatedValue={this.state.role}
                />

                <div className="additional-role-label">{i18n.t("administration.addUser.permissionsSection.additionalRole")}</div>
                <Checkbox
                    label={`${i18n.t("administration.roles.helpdesk")} ${i18n.t("administration.addUser.permissionsSection.resetPasswordInfo")}`}
                    validatedValue={this.state.isHelpdesk}
                    testId="is-helpdesk-checkbox"
                />
            </div>
        );
    }

    private get languages(): DropDownModel[] {
        return [
            {id: "pl", name: `${i18n.t("administration.languages.pl")}`},
            {id: "en", name: `${i18n.t("administration.languages.en")}`}
        ];
    }

    private get roles(): RadioModel[] {
        return [
            {value: "Supervisor", label: `${i18n.t("administration.roles.supervisor")}`},
            {value: "Observer", label: `${i18n.t("administration.roles.observer")}`},
            {value: "Depo", label: `${i18n.t("administration.roles.depo")}`}
        ];
    }

    public get isValid() {
        this.state.username.validate();
        this.state.language.validate();
        this.state.role.validate();

        if (!this.props.isUpdate) {
            this.state.password.validate();
            this.state.confirmPassword.validate();
        }

        return this.state.username.isValid && this.state.language.isValid && this.state.password.isValid &&
            this.state.confirmPassword.isValid && this.state.role.isValid;
    }


    private async submit() {
        this.setState({response: undefined});
        if (this.isValid) {
            this.setState({isSubmitButtonBlocked: true});
            await this.addOrModifyUser();
            this.setState({isSubmitButtonBlocked: false});
        }
        Utils.scrollToFirstErrorMessage();
    }


    private async addOrModifyUser() {
        if (this.props.isUpdate && this.props.userData?.id) {
            await this.modifyUser();
        } else {
            await this.addUser();
        }

        if (Api.isSuccess(this.state.response)) {
            this.props.history.push(routingAddresses.administration.list);
        }
    }

    private async addUser() {
        this.setState({
            response: await AdministrationService.addUser({
                role: this.state.role.value || "",
                username: this.state.username.value.trim(),
                isHelpdesk: this.state.isHelpdesk.value,
                language: this.state.language.value,
                password: this.state.password.value
            })
        });
    }

    private async modifyUser() {
        this.setState({
            response:
                await AdministrationService.modifyUser(this.props.userData!.id, {
                    role: this.state.role.value || "",
                    isHelpdesk: this.state.isHelpdesk.value,
                    language: this.state.language.value
                })
        });
    }

    private setUserData() {
        this.state.username.setValue(this.props.userData?.username || "");
        this.state.language.setValue(this.props.userData?.language || "");
        this.state.role.setValue(this.props.userData?.role || null);
        this.state.isHelpdesk.setValue(this.props.userData?.isHelpdesk || false);
    }

    private onResetOrChangePasswordAction(displayPopup: "resetPasswordPopup" | "changePasswordPopup" | null) {
        this.setState({displayPopup: displayPopup});
    }

    private closePopup() {
        this.setState({displayPopup: null, resetPasswordResponse: undefined});
    }

    private async confirmResetPassword() {
        this.setState({resetPasswordResponse: undefined, isPopupActionWaiting: true});

        if (this.props.isUpdate && this.props.userData?.id) {
            this.setState({resetPasswordResponse: await AdministrationService.resetUserPassword(this.props.userData!.id)});
        }

        if (Api.isSuccess(this.state.resetPasswordResponse)) {
            this.setState({
                displayPopup: null
            });
        }
        this.setState({isPopupActionWaiting: false});
    }

    private async confirmEnableOrDisable(popupType: "enablePopup" | "disablePopup", enable: boolean) {
        this.setState({
            isPopupActionWaiting: true,
            enableUserResponse: undefined,
            disableUserResponse: undefined
        });

        if (this.props.isUpdate && this.props.userData?.id) {
            const response = await AdministrationService.enableOrDisableUser(this.props.userData.id, enable);

            if (enable) {
                this.setState({enableUserResponse: response});
            } else {
                this.setState({disableUserResponse: response});
            }

            if (Api.isSuccess(response)) {
                this.setState({
                    displayPopup: null
                });
                window.location.reload();
            }
            this.closePopup();
        }

        this.setState({isPopupActionWaiting: false});
    }
}

export default withRouter(UserForm);
